import { ApiStandardResponse, IFilterOptions } from '@quadminds/shared/commons';
import { ResponseWithMeta } from '@quadminds/shared/tracking-system';
import { sleep, withRateLimit } from '@quadminds/shared/utils';
import { isAxiosError } from 'axios';
import { IFindAllGrpcRequest } from '../interfaces';

export async function forEachPage<T, K = IFilterOptions>(
    apiCall: (filters: Partial<K>) => Promise<ApiStandardResponse<T[]>>,
    apiFilters?: K & Partial<IFilterOptions>,
    callback?: (data: T[], page: number, totalPages: number) => void,
): Promise<void> {
    let currentPage = 1;
    let totalPages = 1;

    const limit = apiFilters?.limit ?? 500;

    while (currentPage <= totalPages) {
        const response = await withRateLimit(
            apiCall({ ...(apiFilters ?? {}), page: currentPage, limit } as unknown as K),
        );
        if (callback) callback(response.data, currentPage, totalPages);
        currentPage = (response.meta.page as number) + 1;
        totalPages = response.meta.totalPages as number;
    }
}

export async function withPaginate<T, K = IFilterOptions>(
    apiCall: (filters: Partial<K>) => Promise<ApiStandardResponse<T[]>>,
    apiFilters?: K & Partial<IFilterOptions>,
    options?: {
        maxRetries?: number;
        debounce?: number;
    }, // Set a maximum retry count
): Promise<T[]> {
    let allData: T[] = [];
    let currentPage = 1;
    let totalPages = 1;

    const limit = apiFilters?.limit ?? 500;
    let { maxRetries = 3 } = options || {};
    const { debounce = 100 } = options || {};

    while (currentPage <= totalPages) {
        try {
            const response = await withRateLimit(
                apiCall({ ...(apiFilters ?? {}), page: currentPage, limit } as unknown as K),
            );
            allData = allData.concat(response.data);
            currentPage = (response.meta.page as number) + 1;
            totalPages = response.meta.totalPages as number;
        } catch (error) {
            // If the error is an axios error and is a client error, throw the error and avoid retrying
            if (isAxiosError(error) && error.response?.status && error.response?.status >= 400) {
                console.error(
                    `API call failed while paginating with status ${error.response?.status}. No retry will be performed.`,
                    error.response?.data?.message,
                );
                throw error;
            }
            // Logs the error if not an axios error
            if (error instanceof Error) console.error('API call failed:', error.message);

            //  Retries if maxRetries is greater than 0
            if (maxRetries > 0) {
                // Implement exponential backoff (e.g., wait 2^n seconds before retrying)
                console.error(`API call failed. Retrying in ${Math.pow(2, 3 - maxRetries)} seconds...`);
                const delay = Math.pow(2, 3 - maxRetries) * 1000;
                await sleep(delay);
                maxRetries--;
            } else {
                throw new Error(`Max retries reached. Unable to paginate. ${apiCall?.name ? apiCall.name : ''}`);
            }
        }
        await sleep(debounce);
    }

    return allData;
}

export async function withPaginateDolphin<T, K = IFilterOptions>(
    apiCall: (filters: Partial<K>) => Promise<ResponseWithMeta<T[]>>,
    apiFilters?: K & Partial<IFilterOptions>,
): Promise<T[]> {
    let allData: T[] = [];
    let currentPage = 1;
    let totalPages = 1;
    const limit = apiFilters?.limit ?? 500;

    while (currentPage <= totalPages) {
        const response = await withRateLimit(
            apiCall({ ...(apiFilters ?? {}), page: currentPage, limit } as unknown as K),
        );
        allData = allData.concat(response.data);
        currentPage = (response.page as number) + 1;
        totalPages = response.totalPages as number;
    }

    return allData;
}

export async function withPaginateGRPC<T>(
    apiCall: (
        filters?: Partial<{ paginateOptions: { page: number; limit: number } }>,
    ) => Promise<{ data: T[]; page: number; totalPages: number }>,
): Promise<T[]> {
    let allData: T[] = [];
    let currentPage = 1;
    let totalPages = 1;
    const limit = 1000;

    while (currentPage <= totalPages) {
        const response = await apiCall({
            paginateOptions: { page: currentPage, limit },
        });
        if (response.data) allData = allData.concat(response.data);

        currentPage = response['page'] + 1;
        totalPages = response['totalPages'];
    }

    return allData;
}

export async function withPaginateGRPC2<T, K = IFindAllGrpcRequest>(
    apiCall: (
        filters?: Partial<{ page: number; limit: number }>,
    ) => Promise<{ values: T[]; page: number; totalPages: number; total: number }>,
    apiFilters?: K & Partial<IFindAllGrpcRequest>,
): Promise<T[]> {
    let allData: T[] = [];
    let currentPage = 1;
    let totalPages = 1;
    const limit = 1000;

    while (currentPage <= totalPages) {
        const response = await apiCall({ ...(apiFilters ?? {}), page: currentPage, limit });
        if (response.values) allData = allData.concat(response.values);

        currentPage = response['page'] + 1;
        totalPages = response['totalPages'];
    }

    return allData;
}
